import { faCircleQuestion } from "@fortawesome/pro-solid-svg-icons/faCircleQuestion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox, Tooltip } from "@mantine/core";
import { useCallback } from "react";

import { useFilterAnalytics } from "@/hooks/analytics.hooks";
import type {
  ArrayKeys,
  ElementOf,
  FilterParamsHandle,
  ParamsConstraint,
} from "@/hooks/use-filter-params";

export interface CheckBoxFilterProps<
  K extends ArrayKeys<F>,
  F extends ParamsConstraint,
> {
  analyticsName: string;
  name: K;
  options?: CheckBoxOption<ElementOf<F[K]>>[];
  filters: FilterParamsHandle<F>;
}

export interface CheckBoxOption<T> {
  display: string;
  helperText?: string;
  value: T;
}

export const convertStringArrayToCheckboxOption = <T extends string>(
  arr: T[],
): CheckBoxOption<T>[] =>
  arr
    .filter((option) => option !== "")
    .map((value) => ({
      display: value,
      value: value,
    }));

export const CheckboxFilter = <
  K extends ArrayKeys<F>,
  F extends ParamsConstraint,
>({
  analyticsName,
  name,
  options = [],
  filters,
}: CheckBoxFilterProps<K, F>) => {
  const trackFilter = useFilterAnalytics(analyticsName);

  const getLabel = useCallback((display: string, helperText?: string) => {
    if (!helperText) {
      return display;
    }

    return (
      <>
        {display}
        <Tooltip
          className="text-center"
          events={{ hover: true, focus: true, touch: true }}
          label={helperText}
          multiline
          w={220}
        >
          <button className="bg-transparent border-0 ml-1.5">
            <FontAwesomeIcon
              color="var(--mantine-color-gray-3)"
              icon={faCircleQuestion}
            />
          </button>
        </Tooltip>
      </>
    );
  }, []);

  return (
    <div
      className="flex flex-col items-start justify-between"
      style={{ maxHeight: 200, overflowY: "auto" }}
    >
      {options?.map(({ display, helperText, value }) => {
        return (
          <Checkbox
            key={display}
            label={getLabel(display, helperText)}
            classNames={{ root: "my-1" }}
            onChange={() => {
              trackFilter(value);
              filters.toggleValue(name, value);
            }}
            checked={filters.has(name, value)}
            size="sm"
          />
        );
      })}
    </div>
  );
};
